import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setDialog } from 'src/features/dialog/dialogSlice';
import {
  useGetTenantInformationQuery,
  useSendTenantTestMailMutation,
  useUpdateTenantMailSettingsMutation,
} from 'src/features/tenants/tenantsApiSlice';
import { selectCurrentUserData } from 'src/features/auth/authSlice';
import MailTemplatePageLayout from './MailTemplatePageLayout';

const DEFAULT_SUBJECT = 'Registrierungslink für Ihr Mitgliederkonto';
const DEFAULT_TEMPLATE =
  '{{formalGreeting}},\n\n' +
  'für {{memberFullName}} wurde ein Zugang zu {{tenantName}} vorbereitet.\n' +
  'Bitte legen Sie über den folgenden Link Ihr Passwort fest:\n' +
  '{{activationUrl}}\n\n' +
  '{{activationValidityText}}\n\n' +
  'Mit freundlichen Grüßen\n' +
  '{{tenantName}}';

const tokens = [
  {
    value: '{{formalGreeting}}',
    label: 'Formale Anrede',
    desc: 'Automatische Anrede basierend auf dem Titel: Herr → Sehr geehrter Herr [Nachname], Frau → Sehr geehrte Frau [Nachname], kein Titel → Guten Tag [Vollständiger Name].',
  },
  {
    value: '{{memberFirstName}}',
    label: 'Vorname',
    desc: 'Vorname des Mitglieds.',
  },
  {
    value: '{{memberLastName}}',
    label: 'Nachname',
    desc: 'Nachname des Mitglieds.',
  },
  {
    value: '{{memberFullName}}',
    label: 'Vollständiger Name',
    desc: 'Vor- und Nachname des Mitglieds.',
  },
  {
    value: '{{activationUrl}}',
    label: 'Registrierungslink',
    desc: 'Individueller Link, über den das Mitglied sein Passwort festlegt und den Zugang aktiviert.',
  },
  {
    value: '{{activationValidityText}}',
    label: 'Link-Gültigkeit',
    desc: 'Beschreibt die im Versanddialog gewählte Gültigkeit des Links, z. B. "Der Link ist 12 Stunden gültig." oder "Der Link ist unbegrenzt gültig."',
  },
  {
    value: '{{tenantName}}',
    label: 'Vereinsname',
    desc: 'Name Ihres Vereins.',
  },
  {
    value: '{{today}}',
    label: 'Heutiges Datum',
    desc: 'Das aktuelle Datum zum Zeitpunkt des E-Mail-Versands.',
  },
];

const MailRegistrationLinkTemplateSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const currentUserData = useSelector(selectCurrentUserData);
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const [updateTenantMailSettings, { isLoading: isSaving }] =
    useUpdateTenantMailSettingsMutation();
  const [sendTenantTestMail, { isLoading: isTesting }] =
    useSendTenantTestMailMutation();

  // Zeige gespeicherten Wert – oder den Backend-Default als Fallback
  const defaultValues = useMemo(
    () => ({
      registrationLinkSubject:
        tenantInfo?.settings?.mail?.registrationLinkSubject || DEFAULT_SUBJECT,
      registrationLinkTemplate:
        tenantInfo?.settings?.mail?.registrationLinkTemplate || DEFAULT_TEMPLATE,
      registrationLinkTemplateMode:
        tenantInfo?.settings?.mail?.registrationLinkTemplateMode || 'auto',
      registrationLinkBcc: tenantInfo?.settings?.mail?.registrationLinkBcc || '',
      testRecipient: currentUserData?.mail || '',
    }),
    [tenantInfo, currentUserData]
  );

  const { register, handleSubmit, control, reset, setValue, getValues } =
    useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleInsertToken = (token) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, token);
      const next = editorRef.current.innerHTML || '';
      setValue('registrationLinkTemplate', next, { shouldDirty: true });
      return;
    }
    const current = getValues('registrationLinkTemplate') || '';
    setValue(
      'registrationLinkTemplate',
      current ? `${current} ${token}` : token,
      { shouldDirty: true }
    );
  };

  const onSubmit = async (data) => {
    const { testRecipient, ...mailSettings } = data;
    try {
      const result = await updateTenantMailSettings(mailSettings).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Einstellungen gespeichert',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message ||
              'Einstellungen konnten nicht gespeichert werden.',
          },
        })
      );
    }
  };

  const handleSendTest = async () => {
    const recipient = (getValues('testRecipient') || '').trim();
    if (!recipient) {
      dispatch(
        setDialog({
          title: 'Hinweis',
          type: 'ERROR',
          data: { errorText: 'Bitte eine Test-E-Mail-Adresse angeben.' },
        })
      );
      return;
    }
    try {
      const result = await sendTenantTestMail({ recipient }).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Test-E-Mail gesendet',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message || 'Test-E-Mail konnte nicht gesendet werden.',
          },
        })
      );
    }
  };

  if (isLoading) return null;

  return (
    <MailTemplatePageLayout
      title='Registrierungslink'
      description='Passen Sie den Text der E-Mail an, die an Mitglieder gesendet wird, wenn ein Admin über „Registrierungslink senden" einen persönlichen Aktivierungslink verschickt. Der Platzhalter {{activationUrl}} ist Pflicht – ohne ihn kann das Mitglied keinen Zugang anlegen.'
      subjectFieldName='registrationLinkSubject'
      templateFieldName='registrationLinkTemplate'
      bccFieldName='registrationLinkBcc'
      templateType='registrationLink'
      modeFieldName='registrationLinkTemplateMode'
      currentHtmlTemplate={tenantInfo?.settings?.mail?.registrationLinkHtmlTemplate}
      tokens={tokens}
      control={control}
      register={register}
      handleInsertToken={handleInsertToken}
      editorRef={editorRef}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      handleSendTest={handleSendTest}
      isSaving={isSaving}
      isTesting={isTesting}
      tenantName={tenantInfo?.tenantName}
      navigate={navigate}
    />
  );
};

export default MailRegistrationLinkTemplateSettings;
